import { useState, useRef, useEffect, useMemo, KeyboardEvent } from 'react';
import { TextInput, Popover, Stack, Text, Group, Badge, ScrollArea } from '@mantine/core';
import { IconTemplate } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { componentsTestIds } from '@components/testids';
import {
  flattenVariablesRecord,
  groupVariablesByKind,
  kindAwareComparator,
  type MaterializedVariable,
  type VariableComparator
} from '@bett3r-dev/teselly-rule-editor';

type MaterializedOption = {
  kind: string;
  label: string;
  value: string;
  description?: string;
};

type TemplateInputProps = {
  label: string;
  description?: string;
  value: string;
  onChange: ( value: string ) => void;
  variables?: Record<string, MaterializedOption[]>;
  required?: boolean;
  placeholder?: string;
  error?: string;
  onValidate?: ( value: string ) => string | null;
  maxLength?: number;
};

type Suggestion = {
  value: string;
  label: string;
  description?: string;
  type: 'variable';
  group?: string;
  originalValue?: string;
};

/**
 * Kebab-case a kind/source label for use in `data-testid` slugs.
 * Strips Latin diacritics so `Items en esta publicación` becomes
 * `items-en-esta-publicacion`. Handles camelCase (`channelCommissions`
 * → `channel-commissions`) by inserting a hyphen at case boundaries.
 */
const kebabCase = ( input: string ): string =>
  input
    .normalize( 'NFD' )
    .replace( /[̀-ͯ]/g, '' )
    .replace( /([a-z0-9])([A-Z])/g, '$1-$2' )
    .toLowerCase()
    .replace( /[^a-z0-9]+/g, '-' )
    .replace( /^-+|-+$/g, '' );

/**
 * Build the chip slug for a `data-testid="rule-editor-palette-chip-${slug}"`.
 * Prefers the `itemId` extracted from the chip's value token (for the
 * publication-bound `quantitiesByItem` / `priceListsByItem` /
 * `priceListsByQuantity` chips). Falls back to the kebab-cased
 * `source:` token when present, then to the kebab-cased `kind`/group.
 */
const buildChipSlug = ( suggestion: Suggestion ): string => {
  const token = suggestion.originalValue ?? suggestion.value;
  const itemIdMatch = /itemId:'([^']+)'/.exec( token );
  if ( itemIdMatch ) return itemIdMatch[ 1 ];
  const sourceMatch = /source:([A-Za-z0-9_]+)/.exec( token );
  if ( sourceMatch ) return kebabCase( sourceMatch[ 1 ]);
  if ( suggestion.group ) return kebabCase( suggestion.group );
  return kebabCase( suggestion.value );
};

export const TemplateInput = ({ 
  label, 
  description, 
  value, 
  onChange, 
  variables = {}, 
  required = false, 
  placeholder = 'Empieza a escribir, podes ver sugerencias con Ctrl+Espacio', 
  error, 
  onValidate,
  maxLength = 500
}: TemplateInputProps ) => {
  const { t } = useTranslation( 'rules' );
  const [ showSuggestions, setShowSuggestions ] = useState( false );
  const [ suggestions, setSuggestions ] = useState<Suggestion[]>([]);
  const [ selectedSuggestionIndex, setSelectedSuggestionIndex ] = useState( 0 );
  const inputRef = useRef<HTMLInputElement>( null );
  const [ displayValue, setDisplayValue ] = useState( '' );

  /**
   * Group variables via the rule-editor package selectors so the section
   * order is data-driven ( per `expose.kind` ) and within-section sort
   * uses `kindAwareComparator`. The "Items en esta publicación" kind
   * defaults to alphabetical-by-label here; a kit-position comparator
   * will plug into the same seam in a follow-up ticket.
   */
  const variableGroups = useMemo(() => {
    const flat = flattenVariablesRecord( variables as Record<string, readonly MaterializedVariable[]> );
    const comparator: VariableComparator = kindAwareComparator({});
    return groupVariablesByKind( flat, comparator );
  }, [variables]);

  // Convert variables to suggestions
  const getVariableSuggestions = (): Suggestion[] => {
    const variableSuggestions: Suggestion[] = [];

    for ( const group of variableGroups ) {
      for ( const variable of group.variables ) {
        variableSuggestions.push({
          value: variable.label,
          label: variable.label,
          description: variable.description,
          type: 'variable' as const,
          group: variable.kind ?? group.kind,
          originalValue: variable.value
        });
      }
    }

    return variableSuggestions;
  };

  const getAllSuggestions = (): Suggestion[] => {
    return getVariableSuggestions();
  };

  const convertStoredToDisplay = ( storedValue: string ): string => {
    let result = storedValue;
    
    const valueToLabelMap: Record<string, string> = {};
    Object.values( variables ).flat()
.forEach( option => {
      if ( option && option.value && option.label ) {
        valueToLabelMap[option.value] = option.label;
      }
    });
    
    const markers: {[key: string]: string } = {};
    let markerCount = 0;
    
    result = result.replace( /\{\{([^}]+)\}\}/g, ( match, capturedValue ) => {
      const label = valueToLabelMap[capturedValue];
      if ( label ) {
        const marker = `__TEMPLATE_DISPLAY_MARKER_${markerCount++}__`;
        markers[marker] = label;
        return marker;
      }
      return match;
    });
    
    for ( const [ marker, label ] of Object.entries( markers )) {
      result = result.replace( marker, label );
    }
    
    return result;
  };

  const convertDisplayToStored = ( displayValue: string ): string => {
    let result = displayValue;
    
    const labelToValueMap: Record<string, string> = {};
    Object.values( variables ).flat()
.forEach( option => {
      if ( option && option.value && option.label ) {
        labelToValueMap[option.label] = option.value;
      }
    });
    
    const sortedLabels = Object.keys( labelToValueMap ).sort(( a, b ) => b.length - a.length );
    
    const markers: {[key: string]: string } = {};
    let markerCount = 0;
    
    for ( const label of sortedLabels ) {
      const value = labelToValueMap[label];
      const marker = `__TEMPLATE_MARKER_${markerCount++}__`;
      
      const regex = new RegExp( `\\b${label.replace( /[.*+?^${}()|[\]\\]/g, '\\$&' )}\\b`, 'g' );
      result = result.replace( regex, marker );
      
      markers[marker] = `{{${value}}}`;
    }
    
    for ( const [ marker, replacement ] of Object.entries( markers )) {
      result = result.replace( marker, replacement );
    }
    
    return result;
  };

  useEffect(() => {
    setDisplayValue( convertStoredToDisplay( value ));
  }, [ value, variables ]);

  const filterSuggestions = ( query: string ): Suggestion[] => {
    if ( !query ) return getAllSuggestions();
    
    const lowercaseQuery = query.toLowerCase();
    const allSuggestions = getAllSuggestions();
    
    return allSuggestions.filter( suggestion => 
      suggestion.value.toLowerCase().includes( lowercaseQuery ) ||
      suggestion.label.toLowerCase().includes( lowercaseQuery ) ||
      ( suggestion.group && suggestion.group.toLowerCase().includes( lowercaseQuery )));
  };

  const handleKeyDown = ( event: KeyboardEvent<HTMLInputElement> ) => {
    // handle ctrlspace for manual suggestion 
    if ( event.ctrlKey && event.key === ' ' ) {
      event.preventDefault();
      const allSuggestions = getAllSuggestions();
      setSuggestions( allSuggestions );
      setSelectedSuggestionIndex( 0 );
      setShowSuggestions( true );
      return;
    }

    if ( showSuggestions ) {
      switch ( event.key ) {
        case 'ArrowDown':
          event.preventDefault();
          setSelectedSuggestionIndex( prev => 
            prev < suggestions.length - 1 ? prev + 1 : 0 );
          break;
        case 'ArrowUp':
          event.preventDefault();
          setSelectedSuggestionIndex( prev => 
            prev > 0 ? prev - 1 : suggestions.length - 1 );
          break;
        case 'Enter':
        case 'Tab':
          event.preventDefault();
          if ( suggestions[selectedSuggestionIndex]) {
            insertSuggestion( suggestions[selectedSuggestionIndex]);
          }
          break;
        case 'Escape':
          setShowSuggestions( false );
          break;
      }
    }
  };

  const getCurrentWord = ( text: string, position: number ): string => {
    const beforeCursor = text.substring( 0, position );
    
    const wordBoundaries = /[\s]/;
    
    let wordStart = position;
    for ( let i = position - 1; i >= 0; i-- ) {
      if ( wordBoundaries.test( text[i])) {
        wordStart = i + 1;
        break;
      }
      if ( i === 0 ) {
        wordStart = 0;
      }
    }
    
    let wordEnd = position;
    for ( let i = position; i < text.length; i++ ) {
      if ( wordBoundaries.test( text[i])) {
        wordEnd = i;
        break;
      }
      if ( i === text.length - 1 ) {
        wordEnd = text.length;
      }
    }
    
    const currentWord = text.substring( wordStart, wordEnd );
    return currentWord.trim();
  };

  const insertSuggestion = ( suggestion: Suggestion ) => {
    const input = inputRef.current;
    if ( !input ) return;

    const start = input.selectionStart || 0;
    const end = input.selectionEnd || 0;
    
    const wordBoundaries = /[\s]/;
    
    let wordStart = start;
    for ( let i = start - 1; i >= 0; i-- ) {
      if ( wordBoundaries.test( displayValue[i])) {
        wordStart = i + 1;
        break;
      }
      if ( i === 0 ) {
        wordStart = 0;
      }
    }
    
    let wordEnd = end;
    for ( let i = end; i < displayValue.length; i++ ) {
      if ( wordBoundaries.test( displayValue[i])) {
        wordEnd = i;
        break;
      }
      if ( i === displayValue.length - 1 ) {
        wordEnd = displayValue.length;
      }
    }
    
    let insertValue = suggestion.value;
    if ( suggestion.type === 'variable' && suggestion.originalValue ) {
      insertValue = suggestion.label; 
    }
    
    const newDisplayValue = 
      displayValue.substring( 0, wordStart ) + 
      insertValue + 
      displayValue.substring( wordEnd );
    
    const newStoredValue = convertDisplayToStored( newDisplayValue );
    onChange( newStoredValue );
    
    if ( onValidate ) {
      const validationError = validateTemplate( newStoredValue );
      onValidate( validationError || '' );
    }
    
    setShowSuggestions( false );
    
    setTimeout(() => {
      const newPosition = wordStart + insertValue.length;
      input.setSelectionRange( newPosition, newPosition );
      input.focus();
    }, 0 );
  };

  const handleInputChange = ( event: React.ChangeEvent<HTMLInputElement> ) => {
    const newDisplayValue = event.currentTarget.value;
    setDisplayValue( newDisplayValue );
    
    const newStoredValue = convertDisplayToStored( newDisplayValue );
    onChange( newStoredValue );
    
    if ( onValidate ) {
      const validationError = validateTemplate( newStoredValue );
      onValidate( validationError || '' );
    }
    
    const cursorPos = event.currentTarget.selectionStart || 0;
    const currentWord = getCurrentWord( newDisplayValue, cursorPos );
    
    if ( currentWord.length > 0 ) {
      const filteredSuggestions = filterSuggestions( currentWord );
      setSuggestions( filteredSuggestions );
      setSelectedSuggestionIndex( 0 );
      setShowSuggestions( filteredSuggestions.length > 0 );
    } else {
      setShowSuggestions( false );
    }
  };

  const handleFocus = () => {
    const allSuggestions = getAllSuggestions();
    if ( allSuggestions.length > 0 ) {
      setSuggestions( allSuggestions );
      setSelectedSuggestionIndex( 0 );
      setShowSuggestions( true );
    }
  };

  const getSuggestionColor = ( type: string ) => {
    return 'blue'; // All suggestions are variables
  };

  const groupedSuggestions = suggestions.reduce(( groups, suggestion ) => {
    const key = suggestion.group || suggestion.type;
    if ( !groups[key]) {
      groups[key] = [];
    }
    groups[key].push( suggestion );
    return groups;
  }, {} as Record<string, Suggestion[]> );

  const validateTemplate = ( storedTemplate: string ): string | null => {
    if ( !storedTemplate.trim()) return null;
    
    // Check if template exceeds max length
    if ( maxLength && storedTemplate.length > maxLength ) {
      return t( 'validation_max_length_exceeded', `La plantilla excede el límite de ${maxLength} caracteres` );
    }
       
    return null;
  };

  const validationError = validateTemplate( value );
  const displayError = error || validationError;

  return (
    <Popover
      opened={showSuggestions}
      onClose={() => setShowSuggestions( false )}
      position="bottom-start"
      width="target"
      shadow="md"
    >
      <Popover.Target>
        <TextInput
          ref={inputRef}
          label={label}
          value={displayValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={() => setTimeout(() => setShowSuggestions( false ), 150 )}
          placeholder={placeholder || t( 'template_input_placeholder', 'Ingresar plantilla (Ctrl+Espacio para sugerencias)' )}
          required={required}
          radius="xs"
          leftSection={<IconTemplate size={16} />}
          description={description || t( 'template_input_description', 'Ctrl+Espacio para sugerencias' )}
          error={displayError}
        />
      </Popover.Target>
      
      <Popover.Dropdown p={0}>
        <ScrollArea h={200} scrollbars="y" type="scroll" scrollHideDelay={0}>
          <Stack gap={0}>
            {Object.entries( groupedSuggestions ).map(([ groupName, groupSuggestions ]) => (
              <div key={groupName}>
                <Text
                  size="xs"
                  fw={500}
                  p="xs"
                  style={{ backgroundColor: 'light-dark(var(--mantine-color-gray-1), var(--mantine-color-dark-5))' }}
                  c="dimmed"
                  data-testid={componentsTestIds.ruleEditorPaletteSection( kebabCase( groupName ))}
                >
                  {t( `suggestion_group_${groupName?.toLowerCase()}`, groupName.charAt( 0 ).toUpperCase() + groupName.slice( 1 ))}
                </Text>
                {groupSuggestions.map(( suggestion, index ) => {
                  const globalIndex = suggestions.indexOf( suggestion );
                  return (
                    <Group
                      key={`${groupName}-${index}`}
                      p="xs"
                      gap="xs"
                      data-testid={componentsTestIds.ruleEditorPaletteChip( buildChipSlug( suggestion ))}
                      style={{
                        cursor: 'pointer',
                        transition: 'background-color 0.2s ease',
                        backgroundColor: globalIndex === selectedSuggestionIndex
                          ? 'light-dark(var(--mantine-color-blue-1), rgba(34, 139, 230, 0.15))'
                          : undefined
                      }}
                      onClick={() => insertSuggestion( suggestion )}
                      onMouseEnter={( e ) => {
                        if ( globalIndex !== selectedSuggestionIndex ) {
                          e.currentTarget.style.backgroundColor = 'light-dark(var(--mantine-color-gray-1), var(--mantine-color-dark-5))';
                        }
                      }}
                      onMouseLeave={( e ) => {
                        if ( globalIndex !== selectedSuggestionIndex ) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      <Badge
                        size="xs"
                        color={getSuggestionColor( suggestion.type )}
                        variant="light"
                      >
                        {suggestion.type}
                      </Badge>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <Text size="sm" fw={500} truncate>
                          {suggestion.label}
                        </Text>
                        {suggestion.description && (
                          <Text size="xs" c="dimmed" lineClamp={2}>
                            {suggestion.description}
                          </Text>
                        )}
                      </div>
                    </Group>
                  );
                })}
              </div>
            ))}
          </Stack>
        </ScrollArea>
      </Popover.Dropdown>
    </Popover>
  );
};